import { useQuery } from '@tanstack/react-query';
import { RI_THRESHOLDS, RiStatus } from '@leopart/calc';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { formatFullName } from '../../../utils/functions';
import { roleLabel } from '../../utils/roleLabel';
import { AthleteListRow, AthleteListTone } from '../AthleteListModal/AthleteListModal.types';

/**
 * Riga per-atleta della heatmap Recovery settimanale (vista dentro il tab
 * Sforzo). Stessa forma della risposta backend `getTeamRecoveryWeek`.
 */
export interface ITeamRecoveryRow {
  athlete: { id: string; firstName: string; lastName: string; position?: string };
  /** RI per giorno, allineato a `days`; null = nessun dato quel giorno. */
  days: (number | null)[];
  weeklyAvg: number | null;
  status: RiStatus | null;
}

export interface ITeamRecoveryWeek {
  week: { start: string; end: string };
  days: string[];
  rows: ITeamRecoveryRow[];
  kpi: { avgRi: number | null; atRiskCount: number };
}

const RI_STATUS_TONE: Record<RiStatus, AthleteListTone> = {
  good: 'ok',
  normal: 'ok',
  warning: 'warn',
  critical: 'alert',
};

/**
 * Righe per la modale "Atleti a rischio" (click sul KPI `atRiskCount`): stesso
 * predicato del conteggio KPI lato backend (readiness.service.ts —
 * `rows.filter(r => r.weeklyAvg != null && r.weeklyAvg < RI_THRESHOLDS.normal)`)
 * — soglie da @leopart/calc, fonte unica.
 */
export function getAtRiskAthleteRows(rows: ITeamRecoveryRow[]): AthleteListRow[] {
  return rows
    .filter(
      (r): r is ITeamRecoveryRow & { weeklyAvg: number; status: RiStatus } =>
        r.weeklyAvg != null && r.weeklyAvg < RI_THRESHOLDS.normal && r.status != null,
    )
    .map((r) => ({
      athleteId: r.athlete.id,
      name: formatFullName(r.athlete.firstName, r.athlete.lastName),
      role: roleLabel(r.athlete.position),
      metricValue: `RI ${r.weeklyAvg}`,
      tone: RI_STATUS_TONE[r.status],
    }));
}

/**
 * Recovery (RI) settimanale di squadra per la settimana `weekIso` (lunedì
 * lun-dom, la STESSA di useTeamEffort → incrocio recovery↔carico). Fetcha solo
 * quando `enabled` (la vista Recovery è attiva), per non pesare sul tab Sforzo.
 */
export function useTeamRecovery(teamId: string, weekIso: string, enabled: boolean) {
  const endpoints = useEndpoints();

  const query = useQuery<ITeamRecoveryWeek>({
    queryKey: ['team-recovery-week', teamId, weekIso],
    queryFn: async () => {
      const { data } = await endpoints.getTeamRecoveryWeek(teamId, { week: weekIso });
      return data as ITeamRecoveryWeek;
    },
    enabled: enabled && !!teamId,
    staleTime: 1000 * 60 * 5,
  });

  return {
    days: query.data?.days ?? [],
    rows: query.data?.rows ?? [],
    kpi: query.data?.kpi ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
